import React, { useState, useMemo, useEffect } from "react";
import {
  Calculator,
  Users as UsersIcon,
  Headphones,
  Scale,
  CalendarDays,
  Mail,
  MessageSquare,
  MessageCircle,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useEtatGlobal, getCabinetIdFilter } from "../EtatGlobal.js";
import { authFetch, httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import bureauTutorials from "../Tutoriel/BureauTutorial";
import useBureauFeed, { daysUntil, NIVEAU_LABEL } from "./useBureauFeed";
import useGmailAccount from "../Mails/useGmailAccount";
import { missionCommencee, DEFAULT_AGO_YEAR, COMPTA_TASK_ORDER } from "../Suivis/Annexe/suiviProdUtils.js";
import { urgencyOf } from "./atoms/UrgencyBadge";
import { INK, INK_2, LINE, BURGUNDY } from "./bureauTokens";
import { normalizeText } from "../../utils/text.js";
import SuggestTile from "./atoms/SuggestTile";
import KpiTile from "./atoms/KpiTile";
import ProfileSwitcher from "./organisms/ProfileSwitcher";
import PriorityFeedCard from "./organisms/PriorityFeedCard";
import RecapCard from "./organisms/RecapCard";
import LmSoumisCard from "./organisms/LmSoumisCard";
import RdvCard from "./organisms/RdvCard";
import ShortcutsPanel from "./organisms/ShortcutsPanel";
import NotesBoard from "./organisms/NotesBoard";
import EcheancesCalendarPopover from "./organisms/EcheancesCalendarPopover";
import SuggestModal from "./organisms/SuggestModal";
import {
  MESSAGERIE_UNREAD_SEED,
  ECHEANCES,
  ECHEANCES_BY_PROFILE,
  computeEcheanceItems,
} from "./bureauSeedData";

const PROFILE_KEY = "myalfa-bureau-profile";

// Mêmes champs que "Dossiers gérés" côté Admin (UserDetailModal.js) — sert à
// filtrer les dossiers SuiviCompta liés au collaborateur connecté.
const CLIENT_OWNER_FIELDS = [
  "id_expertComptable",
  "id_superviseur",
  "id_collaborateur",
  "id_collaborateur_niv2",
  "id_social",
  "id_juridique",
];

// Libellé + couleur par statut LmValidation — carte "LM Soumis" (profil
// Secrétariat), voir lmSoumisItems plus bas.
const LM_SOUMIS_STATUT_META = {
  EN_ATTENTE: { label: "En attente", tone: "warn" },
  VALIDE: { label: "Validé", tone: "ok" },
  REFUSE: { label: "Refusé", tone: "bad" },
};

// Mêmes 19 tables/labels que le détail SuiviSocial.js (tableLabels) — sert à
// construire "Événements sociaux récents" à partir des vraies données.
const SOCIAL_EVENT_LABELS = {
  AttestationAT: "Attestation papier AT",
  DSNMaladieAT: "DSN Maladie ou AT",
  AttestationMaternitePaternite: "Attestation maternité / paternité",
  ChomagePartielle: "Chômage partielle",
  Dpae: "DPAE",
  ContratDetravail: "Contrat de travail",
  Avenant: "Avenant",
  CertiicatTravail: "Certificat de travail",
  AttestationPole: "Attestation Pôle Emploi",
  RecuSolde: "Reçu pour solde de tout compte",
  RuptureConventionnelle: "Rupture conventionnelle",
  Licenciement: "Licenciement",
  Demission: "Démission",
  FinPeriodedEssai: "Fin période d'essai",
  FinContratPro: "Fin contrat de pro",
  FinContratApprenti: "Fin contrat d'apprenti",
  RuptureCommun: "Rupture commun",
  FinCdd: "Fin CDD",
  CourriersDivers: "Courriers divers",
};

const PROFILES = [
  { id: "comptable", label: "Comptable", icon: Calculator },
  { id: "paie", label: "Gestion de paie", icon: UsersIcon },
  { id: "secretariat", label: "Secrétariat / Direction", icon: Headphones },
  { id: "juriste", label: "Juridique", icon: Scale },
];

// Profil verrouillé d'après le rôle réel de l'utilisateur (JURIDIQUE/SOCIAL/
// SECRETARIAT ont chacun leur profil dédié). Pour les rôles génériques
// (COLLABORATEUR/ASSISTANT/EXPERT_COMPTABLE/SUPERVISEUR, qui ne distinguent
// pas un juriste/gestionnaire de paie d'un comptable), le rôle seul ne
// suffit pas à déterminer l'accès — on regarde alors la "Fonction" (texte
// libre, ex: "Juriste" pour un COLLABORATEUR) en repli.
const ROLE_TO_PROFILE = {
  JURIDIQUE: "juriste",
  SOCIAL: "paie",
  SECRETARIAT: "secretariat",
};

// Profil déterminé en priorité par le champ "Pôle" (fermé, indépendant du
// rôle/permissions — voir enum Pole côté backend et l'écran Admin) : un
// collaborateur peut avoir un Pôle différent de son rôle/poste réel (ex: un
// juriste dont le Pôle est "Social" parce qu'il travaille en réalité sur le
// social/paie). ROLE_TO_PROFILE et le texte libre du poste ne servent plus
// que de filet de sécurité pour les comptes sans Pôle renseigné.
const POLE_TO_PROFILE = {
  COMPTABILITE: "comptable",
  SOCIAL: "paie",
  SECRETARIAT: "secretariat",
  JURIDIQUE: "juriste",
};

// Filet de sécurité tant que la migration Prisma ajoutant "pole" et son
// backfill (scripts/backfillUserPole.js) ne sont pas confirmés en prod — à
// retirer une fois que c'est fait, puisque son pole sera alors directement
// "SOCIAL" en base.
const PROFILE_OVERRIDE_BY_USER_ID = {
  44: "paie", // GAUDIN Meilys — juriste rattachée au pôle social/paie, pas société
};

function profileForUser(utilisateur) {
  const byPole = POLE_TO_PROFILE[utilisateur?.pole];
  if (byPole) return byPole;

  const override = PROFILE_OVERRIDE_BY_USER_ID[utilisateur?.id];
  if (override) return override;

  const byRole = ROLE_TO_PROFILE[utilisateur?.role];
  if (byRole) return byRole;

  const poste = normalizeText(utilisateur?.poste);
  if (poste.includes("juridique") || poste.includes("juriste")) return "juriste";
  if (poste.includes("social") || poste.includes("paie")) return "paie";
  if (poste.includes("secretariat")) return "secretariat";
  return "comptable";
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

// Même calcul que le badge "Relancer" de PropositionLMLead.js (J-7) et que
// "Relances à venir" de Dashboard.js — décalé à J-6 pour prévenir la veille
// de la relance automatique.
function getDaysDiff(date) {
  const today = new Date();
  const created = new Date(date);
  today.setHours(0, 0, 0, 0);
  created.setHours(0, 0, 0, 0);
  return Math.floor((today - created) / (1000 * 60 * 60 * 24));
}
function getDocDisplayName(doc) {
  const name = doc.emailSubject?.split(" - ")[0]?.trim();
  return !name || name.toLowerCase() === "n/a" ? `${doc.nom || "-"} ${doc.prenom || "-"}` : name;
}
function getDocLMType(doc) {
  return doc.emailSubject?.split(" - ")[1]?.trim() || "";
}

// Bureau — cockpit personnel du collaborateur connecté. Les 4 profils
// partagent tous "Ma file de priorité" (mes todos, via useBureauFeed.js) ;
// chacun a ensuite sa propre carte recap secondaire branchée sur de vraies
// données : Comptable → Traitements comptables en cours, Paie → événements
// sociaux récents, Juridique → échéances de missions ponctuelles dépassées
// (+ carte missions en cours dédiée), Secrétariat → accueil & rendez-vous.
function Bureau({ onNav }) {
  const {
    utilisateur,
    patchTodoLocal,
    juridiquePonctuelleData,
    suiviJuridiqueData,
    suiviComptaData,
    suiviSocialData,
    traitementsComptablesData,
    isLoading,
    evenements,
    leads,
    documents,
    googleCalendarConnected,
    googleCalendarUpcomingEvents,
    loadingGoogleCalendarOverview,
    discussionUnreadCount,
  } = useEtatGlobal();
  const { feed, enCoursItems } = useBureauFeed();
  const gmail = useGmailAccount();

  // Seul un administrateur (ou accès tous cabinets) garde le libre choix des
  // 4 profils, mémorisé comme avant — tout autre rôle est verrouillé sur le
  // profil correspondant à son propre rôle, sans possibilité de basculer
  // vers un autre domaine (ex: Secrétariat ne doit pas voir Paie/Juridique).
  const isAdmin = utilisateur?.role === "ADMINISTRATEUR" || utilisateur?.allCabinetsAccess;
  const allowedProfiles = isAdmin ? PROFILES : PROFILES.filter((p) => p.id === profileForUser(utilisateur));

  const [profile, setProfileState] = useState(() => {
    if (!isAdmin) return profileForUser(utilisateur);
    try {
      return localStorage.getItem(PROFILE_KEY) || profileForUser(utilisateur);
    } catch (e) {
      return profileForUser(utilisateur);
    }
  });
  const setProfile = (p) => {
    if (!isAdmin) return;
    setProfileState(p);
    try {
      localStorage.setItem(PROFILE_KEY, p);
    } catch (e) {}
  };

  // Recalcule le profil quand l'utilisateur courant change (ex: "Se
  // connecter en tant que" via l'impersonation, qui ne démonte PAS le
  // Bureau si on y est déjà) — sans ça, le useState ci-dessus ne s'exécute
  // qu'au tout premier montage et le profil affiché reste celui de la
  // précédente identité (bug identifié : Kéren/Nadia/Elisa voyaient des
  // bureaux différents alors qu'elles ont exactement le même rôle/poste en
  // base — c'était juste l'ordre de test qui "collait").
  useEffect(() => {
    if (!isAdmin) {
      setProfileState(profileForUser(utilisateur));
      return;
    }
    try {
      setProfileState(localStorage.getItem(PROFILE_KEY) || profileForUser(utilisateur));
    } catch (e) {
      setProfileState(profileForUser(utilisateur));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilisateur?.id]);

  const [calOpen, setCalOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [toast, setToast] = useState("");

  const flashToast = (message, duration = 1800) => {
    setToast(message);
    setTimeout(() => setToast(""), duration);
  };

  const openItem = (item) => {
    if (item.kind === "task" || item.kind === "etape") {
      onNav && onNav(item.kind === "task" ? "todo" : "traitements");
      return;
    }
    if (item.kind === "mission") {
      onNav && onNav("suivi_juridique");
      return;
    }
    if (item.kind === "paie") {
      onNav && onNav("suivi_social");
      return;
    }
    if (item.kind === "rdv_secretariat") {
      onNav && onNav("agenda");
      return;
    }
    if (item.kind === "prospect_rdv" || item.kind === "lm_relance") {
      onNav && onNav("lead");
      return;
    }
    flashToast("Exemple de démonstration — écran à venir");
  };

  const completeTask = async (item) => {
    patchTodoLocal(item.todoId, { status: "Terminer" });
    flashToast("Tâche clôturée", 1400);
    try {
      await authFetch(`https://backend-myalfa.vercel.app/api/todos/${item.todoId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Terminer" }),
      });
    } catch (err) {
      console.error("Erreur lors de la clôture de la tâche depuis le Bureau :", err);
    }
  };

  const profMeta = PROFILES.find((p) => p.id === profile) || PROFILES[0];

  const cfg = useMemo(() => {
    if (profile === "paie") {
      // KPI "BS à préparer" / "DSN à transmettre" : vraies données de
      // SuiviSocial.js (suiviSocialData, déjà chargées à la connexion via
      // fetchAll → fetchSuiviSocial) — vue globale (tous les dossiers
      // mission sociale, pas seulement les miens), mois en cours. Voir
      // analyse validée avec Jonathan.
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      const clientsSociaux = (suiviSocialData || []).filter((client) =>
        missionCommencee(client, currentYear)
      );
      const getRecord = (client) =>
        client.suiviSocial?.find(
          (e) => e.mois === currentMonth && e.annees === currentYear
        );
      const bsAPreparerCount = clientsSociaux.filter(
        (c) => getRecord(c)?.preparationBS !== "oui"
      ).length;
      const dsnATransmettreCount = clientsSociaux.filter(
        (c) => getRecord(c)?.dsn !== "oui"
      ).length;

      // "Événements sociaux récents" : les 5 derniers événements saisis
      // (toutes catégories confondues — Attestation AT, DPAE, Licenciement,
      // etc.), tous dossiers, triés par date de saisie.
      const daysAgo = (d) => {
        if (!d) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const date = new Date(d);
        date.setHours(0, 0, 0, 0);
        return Math.max(0, Math.round((today - date) / 86400000));
      };
      const recentEvents = [];
      clientsSociaux.forEach((client) => {
        (client.suiviSocial || []).forEach((monthRecord) => {
          Object.keys(SOCIAL_EVENT_LABELS).forEach((type) => {
            (monthRecord[type] || []).forEach((evt) => {
              recentEvents.push({
                id: `${type}-${evt.id}`,
                kind: "paie",
                client: client.nom,
                sub: SOCIAL_EVENT_LABELS[type],
                createdAt: evt.createdAt,
              });
            });
          });
        });
      });
      const recapItems = recentEvents
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5)
        .map((e) => ({
          id: e.id,
          kind: e.kind,
          client: e.client,
          sub: e.sub,
          right: `J-${daysAgo(e.createdAt)}`,
          tone: "ok",
        }));

      return {
        // Même notion que le profil Comptable : mes todos ouverts, pas de
        // liste "file paie" distincte (voir analyse validée avec Jonathan).
        feedTitle: "Ma file de priorité",
        feed,
        feedPageSize: 10,
        seeAll: () => onNav && onNav("todo"),
        recapTitle: "Événements sociaux récents",
        recapItems,
        kpis: [
          {
            key: "bs",
            label: "BS à préparer",
            value: bsAPreparerCount,
            sub: "ce mois, tous dossiers",
            tone: bsAPreparerCount > 0 ? "warn" : "ok",
            onClick: () => onNav && onNav("suivi_social"),
          },
          {
            key: "dsn",
            label: "DSN à transmettre",
            value: dsnATransmettreCount,
            sub: "ce mois, tous dossiers",
            tone: dsnATransmettreCount > 0 ? "warn" : "ok",
            onClick: () => onNav && onNav("suivi_social"),
          },
          {
            key: "dossiers",
            label: "Dossiers paie actifs",
            value: clientsSociaux.length,
            sub: "mission sociale",
            tone: "ok",
            onClick: () => onNav && onNav("suivi_social"),
          },
        ],
        echeances: computeEcheanceItems(ECHEANCES_BY_PROFILE.paie),
      };
    }
    if (profile === "secretariat") {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      // "Accueil & rendez-vous" : vue globale (tout le cabinet, pas
      // seulement les miens — contrairement à upcomingRdvs plus bas, réservé
      // au widget latéral partagé "Mes rendez-vous à venir") — fusion RDV
      // internes (Planning), Google Calendar (le mien, seule source
      // disponible), et RDV prospects (AllLead.js).
      const internalRdvs = (evenements || [])
        .filter(
          (e) => e.categorie === "Demande de rdv" && e.dateRdv && new Date(e.dateRdv) >= now
        )
        .map((e) => ({
          id: `sec-ev-${e.id}`,
          kind: "rdv_secretariat",
          client:
            e.client?.nom ||
            (e.formLead ? `${e.formLead.prenom || ""} ${e.formLead.nom || ""}`.trim() : "") ||
            e.categorie,
          title: "RDV interne",
          detail: e.descriptif || e.note || e.siteRdv || "",
          due: new Date(e.dateRdv),
        }));

      const googleRdvs = googleCalendarConnected
        ? (googleCalendarUpcomingEvents || [])
            .filter((e) => !e.allDay)
            .map((e) => ({
              id: `sec-g-${e.id}`,
              kind: "rdv_secretariat",
              client: e.title,
              title: "Google Calendar",
              detail: e.description || "",
              due: new Date(e.start),
            }))
        : [];

      // Même filtre que la colonne "RDV futur" de AllLead.js (RDVLead.js).
      const prospectRdvs = (leads || [])
        .filter((lead) => lead.specificite === "rdv" && lead.status !== "LM")
        .filter((lead) => {
          if (!lead.dateRDV) return false;
          const leadDate = new Date(lead.dateRDV);
          leadDate.setHours(0, 0, 0, 0);
          return leadDate >= now;
        })
        .map((lead) => ({
          id: `sec-lead-${lead.id}`,
          kind: "prospect_rdv",
          client: `${lead.nom || ""} ${lead.prenom || ""}`.trim() || "-",
          title: "RDV prospect",
          detail: lead.lieuRDV || "",
          due: new Date(lead.dateRDV),
        }));

      const dueRank = (d) => (d ? d.getTime() : Infinity);
      const accueilFeed = [...internalRdvs, ...googleRdvs, ...prospectRdvs].sort(
        (a, b) => dueRank(a.due) - dueRank(b.due)
      );

      // "Ma file de priorité" (ToDo) prend la place principale — comme les
      // 3 autres profils, voir retour Meilys/Jonathan. "Accueil & rendez-
      // vous" (fusion RDV internes/Google/prospects) devient la carte recap
      // secondaire, à la place de "Prospects — RDV à venir" (qui n'était
      // qu'un sous-ensemble de cette même fusion).
      const recapItems = accueilFeed.slice(0, 8).map((r) => {
        const days = daysUntil(r.due);
        return {
          id: r.id,
          kind: r.kind,
          client: r.client,
          sub: r.detail || r.title || "",
          right: days === 0 ? "Aujourd'hui" : days !== null ? `J-${days}` : "",
          tone: days !== null && days <= 1 ? "warn" : "ok",
        };
      });

      return {
        feedTitle: "Ma file de priorité",
        feed,
        feedPageSize: 10,
        seeAll: () => onNav && onNav("todo"),
        recapTitle: "Accueil & rendez-vous",
        recapItems,
        // Pas de KPI pour ce profil — 3 tuiles "Suggérer une idée" à la
        // place (voir le rendu de la grille KPI plus bas).
        kpis: [],
        echeances: computeEcheanceItems(ECHEANCES),
        rdvTitle: "Rendez-vous du cabinet",
      };
    }
    if (profile === "juriste") {
      // Nom de dossier affiché pour une mission JuridiquePonctuelle : les
      // creationsct sont liées à un formLead (pas encore client), les 4
      // autres sources à un vrai client.
      const missionClientLabel = (m) =>
        m.source === "creationsct"
          ? m.nomSociete ||
            [m.formLead?.nom, m.formLead?.prenom].filter(Boolean).join(" ").trim() ||
            "-"
          : m.client?.nom || "-";
      const daysAgo = (d) => {
        if (!d) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const date = new Date(d);
        date.setHours(0, 0, 0, 0);
        return Math.max(0, Math.round((today - date) / 86400000));
      };

      // Missions en cours = vraies données de JuridiquePonctuelle.js
      // (droitsoc/modifsct/creationsct/radiation/autresMission), pas de mock —
      // même source que l'écran Suivi > Missions juridiques ponctuelles.
      const activeMissions = juridiquePonctuelleData.filter(
        (m) => m.status !== "Terminé" && m.status !== "Archiver"
      );
      const dueRank = (d) => (d ? d.getTime() : Infinity);
      const juriFeed = activeMissions
        .map((m) => ({
          id: `${m.source}-${m.id}`,
          kind: "mission",
          client: missionClientLabel(m),
          title: m.missionLabel,
          detail: m.descriptif,
          due: toDate(m.dueDate),
          priority: m.priority,
        }))
        .sort((a, b) => dueRank(a.due) - dueRank(b.due));
      // AGO restant à faire = même calcul que la tuile "AGO en cours" de
      // SuiviJuridiqueAGO.js (missionsEnCours) : même année par défaut
      // (DEFAULT_AGO_YEAR, partagée avec le selectedYear initial de cet
      // écran — voir suiviProdUtils.js), mêmes exclusions (mission pas
      // encore commencée, "1er bilan en N+1" traité à part).
      const AGO_YEAR = DEFAULT_AGO_YEAR;
      const agoCount = suiviJuridiqueData.filter((client) => {
        if (!missionCommencee(client, AGO_YEAR)) return false;
        const clotureYear = new Date(client.dateCloture).getFullYear();
        const hasBilanForYear = client.suiviBilans?.some((b) => b.annees === AGO_YEAR);
        if (!(AGO_YEAR >= clotureYear || hasBilanForYear)) return false;
        const suivi = client.suiviJuridique?.find((s) => s.annees === AGO_YEAR);
        if (suivi?.status === "1er bilan en N+1") return false;
        const suiviBilan = client.suiviBilans?.find((b) => b.annees === AGO_YEAR);
        const isBilanN1 = suiviBilan?.status === "1er bilan en N+1";
        return suivi?.status !== "Terminé" && !isBilanN1;
      }).length;
      const formaliteCount = activeMissions.filter((m) => m.status === "Formalité à régulariser").length;
      // "Échéances dépassées" : missions encore ouvertes (activeMissions) dont
      // la date d'échéance est strictement passée (pas juste "aujourd'hui").
      const isOverdue = (dueDateRaw) => {
        const due = toDate(dueDateRaw);
        if (!due) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        return due < today;
      };
      // "Ma file de priorité" (ToDo) prend la place principale — comme les
      // 3 autres profils, voir retour Meilys/Jonathan. "Mes missions
      // juridiques ponctuelles" ne disparaît pas : elle devient une carte
      // recap dédiée (missionsRecapItems, rendue juste avant "Échéances
      // dépassées" — voir JSX), sur le même modèle que "Relances à venir"
      // pour le profil secretariat.
      const missionsRecapItems = juriFeed.slice(0, 5).map((m) => {
        const days = daysUntil(m.due);
        const late = days !== null && days < 0;
        const right = late ? `Retard ${Math.abs(days)} j` : days !== null ? `J-${days}` : "En cours";
        const tone = late ? "bad" : days !== null && days <= 2 ? "warn" : "ok";
        return { id: m.id, client: m.client, sub: m.title, right, tone };
      });

      return {
        feedTitle: "Ma file de priorité",
        feed,
        feedPageSize: 10,
        seeAll: () => onNav && onNav("todo"),
        missionsRecapTitle: "Mes missions juridiques ponctuelles",
        missionsRecapItems,
        recapTitle: "Échéances dépassées",
        recapItems: activeMissions
          .filter((m) => isOverdue(m.dueDate))
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
          .slice(0, 5)
          .map((m) => ({
            id: `${m.source}-${m.id}`,
            client: missionClientLabel(m),
            sub: m.missionLabel,
            right: `Retard ${daysAgo(m.dueDate)} j`,
            tone: "bad",
          })),
        kpis: [
          { key: "missions", label: "Missions ponctuelles en cours", value: activeMissions.length, sub: "missions ponctuelles", tone: "ok" },
          { key: "ago", label: "AGO restant à faire", value: agoCount, sub: `exercice ${AGO_YEAR}`, tone: "warn" },
          { key: "formalite", label: "Formalités à régulariser", value: formaliteCount, sub: "RCS / greffe", tone: formaliteCount > 0 ? "bad" : "ok" },
        ],
        echeances: computeEcheanceItems(ECHEANCES_BY_PROFILE.juriste),
      };
    }
    // Comptable — profil par défaut, seul branché sur de vraies données.
    // Uniquement mes todos (ToDo.js) — pas de Traitements comptables ici,
    // voir analyse validée avec Jonathan.

    // "TVA mensuelle en retard" : dossiers du tableau SuiviCompta.js (mois en
    // cours) qui me sont liés (mêmes champs que "Dossiers gérés" côté Admin
    // — UserDetailModal.js) et dont le mois en cours n'a pas ses 5 missions
    // complétées (même dérivation que la barre de progression de
    // SuiviCompta.js).
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const tvaMensuelleEnRetard = (suiviComptaData || []).filter((client) => {
      const estLieAMoi = CLIENT_OWNER_FIELDS.some(
        (field) => client[field] === utilisateur?.id
      );
      if (!estLieAMoi) return false;
      if (!missionCommencee(client, currentYear)) return false;
      const mois = client.Compta?.[0]?.mois.find(
        (m) => m.numero === currentMonth && m.annee === currentYear
      );
      const tachesCompletes = COMPTA_TASK_ORDER.filter((type) =>
        mois?.taches?.some((t) => t.type === type && t.statut === true)
      ).length;
      return tachesCompletes < COMPTA_TASK_ORDER.length;
    }).length;

    // "Mes traitements comptables en cours" : étapes de Traitements
    // comptables (Suivi Prod > Traitements comptables) où je suis assigné et
    // dont le statut dérivé est EN_COURS — même donnée/dérivation que
    // SuiviTraitementsComptables.js, pas les todos (voir retour Jonathan :
    // cette carte doit refléter la charge de traitement, pas les todos qui
    // sont déjà dans "Ma file de priorité" juste à côté).
    const dueRank = (d) => (d ? new Date(d).getTime() : Infinity);
    const mesEtapesEnCours = (traitementsComptablesData || [])
      .flatMap((ticket) =>
        (ticket.etapes || [])
          .filter((e) => e.statut === "EN_COURS" && e.assignee?.id === utilisateur?.id)
          .map((etape) => ({ ticket, etape }))
      )
      .sort((a, b) => dueRank(a.etape.dueDate) - dueRank(b.etape.dueDate));

    return {
      feedTitle: "Ma file de priorité",
      feed,
      feedPageSize: 10,
      seeAll: () => onNav && onNav("todo"),
      recapTitle: "Mes traitements comptables en cours",
      recapItems: mesEtapesEnCours.slice(0, 5).map(({ ticket, etape }) => {
        const days = daysUntil(etape.dueDate);
        const late = !!etape.enRetardLive;
        const right = late ? `Retard ${Math.abs(days)} j` : days !== null ? `J-${days}` : "En cours";
        const tone = late ? "bad" : days !== null && days <= 2 ? "warn" : "ok";
        return {
          id: `traitement-${ticket.id}-${etape.id}`,
          client: ticket.client?.nom || "-",
          sub: NIVEAU_LABEL[etape.niveau] || "",
          right,
          tone,
        };
      }),
      kpis: [
        { key: "portefeuille", label: "Mon portefeuille", value: feed.length, sub: "tâches suivies", tone: "ok", onClick: () => onNav && onNav("todo") },
        { key: "encours", label: "Mes tâches en cours/retard", value: enCoursItems.length, sub: "mes todos", tone: "ok", onClick: () => onNav && onNav("todo") },
        {
          key: "tva_mensuelle",
          label: "TVA mensuelle en retard",
          value: tvaMensuelleEnRetard,
          sub: "mes dossiers, mois en cours",
          tone: tvaMensuelleEnRetard > 0 ? "bad" : "ok",
          onClick: () => onNav && onNav("suivi_compta"),
        },
      ],
      echeances: computeEcheanceItems(ECHEANCES),
    };
  }, [
    profile,
    feed,
    enCoursItems,
    onNav,
    juridiquePonctuelleData,
    suiviJuridiqueData,
    suiviComptaData,
    suiviSocialData,
    traitementsComptablesData,
    utilisateur,
    evenements,
    leads,
    googleCalendarConnected,
    googleCalendarUpcomingEvents,
  ]);

  const lateCount = cfg.feed.filter((i) => urgencyOf(i.due).tone === "bad").length;

  const unreadCount = gmail.messages.filter((m) => m.unread).length;

  // "Mes rendez-vous à venir" — vraies données du planning (même source que
  // Planning.jsx) : MES RDV internes uniquement (Evenement "Demande de rdv"
  // où je suis assigné), fusionnés avec MON Google Calendar personnel si
  // connecté — jamais les RDV des autres collaborateurs.
  const upcomingRdvs = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const internal = (evenements || [])
      .filter(
        (e) =>
          e.categorie === "Demande de rdv" &&
          e.dateRdv &&
          new Date(e.dateRdv) >= now &&
          (e.users || []).some((u) => u.id === utilisateur?.id)
      )
      .map((e) => ({
        id: `ev-${e.id}`,
        client:
          e.client?.nom ||
          (e.formLead ? `${e.formLead.prenom || ""} ${e.formLead.nom || ""}`.trim() : "") ||
          e.categorie,
        motif: e.descriptif || e.note || "",
        canal: e.siteRdv || "Interne",
        date: e.dateRdv,
        time: e.heureRdv || "00:00",
      }));

    const google = googleCalendarConnected
      ? (googleCalendarUpcomingEvents || [])
          .filter((e) => !e.allDay)
          .map((e) => {
            const start = new Date(e.start);
            return {
              id: `g-${e.id}`,
              client: e.title,
              motif: e.description || "",
              canal: "Google Calendar",
              date: e.start,
              time: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
            };
          })
      : [];

    return [...internal, ...google]
      .sort((a, b) => new Date(a.date) - new Date(b.date) || a.time.localeCompare(b.time))
      .slice(0, 5);
  }, [evenements, utilisateur, googleCalendarConnected, googleCalendarUpcomingEvents]);

  // "Rendez-vous du cabinet" (profil Secrétariat uniquement) — exactement ce
  // que /planning afficherait pour ce collaborateur : ses propres RDV
  // internes + ceux des collègues qui lui ont partagé leur planning (même
  // mécanisme que Planning.jsx : GET /api/planning-shares/received puis
  // GET /api/evenements/planning?userIds=...), fusionnés avec son propre
  // Google Calendar (aucun partage de Google Calendar entre collaborateurs
  // aujourd'hui — voir Planning.jsx, qui ne superpose lui aussi que le sien).
  const [sharedWithMeUsers, setSharedWithMeUsers] = useState([]);
  const [cabinetInternalEvents, setCabinetInternalEvents] = useState([]);

  useEffect(() => {
    if (profile !== "secretariat" || !utilisateur?.id) return;
    httpClient
      .get("/api/planning-shares/received")
      .then(({ data }) => setSharedWithMeUsers(data.data || []))
      .catch((err) => console.error("Erreur récupération partages planning :", err));
  }, [profile, utilisateur?.id]);

  useEffect(() => {
    if (profile !== "secretariat" || !utilisateur?.id) return;
    const now = new Date();
    const in60Days = new Date(now.getTime() + 60 * 86400000);
    const userIds = [utilisateur.id, ...sharedWithMeUsers.map((s) => s.ownerId)].join(",");
    httpClient
      .get("/api/evenements/planning", {
        params: { userIds, startDate: now.toISOString(), endDate: in60Days.toISOString() },
      })
      .then(({ data }) => setCabinetInternalEvents(data || []))
      .catch((err) => console.error("Erreur récupération planning cabinet :", err));
  }, [profile, utilisateur?.id, sharedWithMeUsers]);

  const cabinetRdvs = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const internal = (cabinetInternalEvents || [])
      .filter((e) => e.dateRdv && new Date(e.dateRdv) >= now)
      .map((e) => ({
        id: `cab-ev-${e.id}`,
        client:
          e.client?.nom ||
          (e.formLead ? `${e.formLead.prenom || ""} ${e.formLead.nom || ""}`.trim() : "") ||
          e.categorie,
        motif: e.descriptif || e.note || "",
        canal: e.siteRdv || "Interne",
        date: e.dateRdv,
        time: e.heureRdv || "00:00",
      }));

    const google = googleCalendarConnected
      ? (googleCalendarUpcomingEvents || [])
          .filter((e) => !e.allDay)
          .map((e) => {
            const start = new Date(e.start);
            return {
              id: `cab-g-${e.id}`,
              client: e.title,
              motif: e.description || "",
              canal: "Google Calendar",
              date: e.start,
              time: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
            };
          })
      : [];

    return [...internal, ...google]
      .sort((a, b) => new Date(a.date) - new Date(b.date) || a.time.localeCompare(b.time))
      .slice(0, 10);
  }, [cabinetInternalEvents, googleCalendarConnected, googleCalendarUpcomingEvents]);

  // "Relances à venir" (profil Secrétariat) — mêmes documents et même calcul
  // que la carte "Relances à venir" de Dashboard.js : propositions de LM à
  // J-6 depuis leur création, qui seront relancées automatiquement demain
  // (J-7) si personne n'intervient.
  const relancesItems = useMemo(() => {
    const cabinetId = getCabinetIdFilter(utilisateur);
    return (documents || [])
      .filter((d) => !cabinetId || d.formLead?.cabinetId === cabinetId)
      .filter(
        (d) =>
          d.specificite !== "completed" &&
          d.specificite !== "archive" &&
          d.status !== "completed" &&
          getDaysDiff(d.createdAt) === 6
      )
      .map((d) => ({
        id: `lm-relance-${d.id}`,
        kind: "lm_relance",
        client: getDocDisplayName(d),
        sub: getDocLMType(d) || "Proposition LM",
        right: "Demain",
        tone: "warn",
      }));
  }, [documents, utilisateur]);

  // "LM Soumis" (profil Secrétariat) — liste brute de LmValidation, sans
  // filtre (cabinet compris) : la demande était d'afficher directement le
  // détail sur la ligne (client, type de LM, expert-comptable, cabinet,
  // statut), pas un système de filtrage.
  const [lmSoumisData, setLmSoumisData] = useState([]);
  const [loadingLmSoumis, setLoadingLmSoumis] = useState(true);

  const fetchLmSoumis = () => {
    if (profile !== "secretariat" || !utilisateur?.id) return;
    setLoadingLmSoumis(true);
    httpClient
      .get("https://backend-myalfa.vercel.app/api/lm-validations")
      .then(({ data }) => setLmSoumisData(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erreur récupération LM soumises :", err))
      .finally(() => setLoadingLmSoumis(false));
  };

  useEffect(fetchLmSoumis, [profile, utilisateur?.id]);

  const lmSoumisItems = useMemo(
    () =>
      (lmSoumisData || [])
        .map((lm) => {
          const meta = LM_SOUMIS_STATUT_META[lm.statut] || {
            label: lm.statut,
            tone: "warn",
          };
          // Le plus récent entre création et dernière réponse (validation/
          // refus) — une vieille LM tout juste traitée doit remonter en
          // tête, pas rester coincée sous des soumissions plus récentes
          // encore en attente.
          const activityAt = lm.respondedAt || lm.createdAt;
          return {
            id: lm.id,
            client: lm.nom_client || lm.societeNom || "—",
            docType: lm.documentTitre || "LM",
            comptable: lm.comptable,
            cabinet: lm.cabinet,
            statutLabel: meta.label,
            tone: meta.tone,
            activityAt,
          };
        })
        .sort((a, b) => new Date(b.activityAt) - new Date(a.activityAt)),
    [lmSoumisData]
  );

  const handleDeleteLmValidation = async (id) => {
    const previous = lmSoumisData;
    setLmSoumisData((prev) => prev.filter((lm) => lm.id !== id));
    try {
      await authFetch(`https://backend-myalfa.vercel.app/api/lm-validations/${id}`, {
        method: "DELETE",
      });
      flashToast("LM soumise supprimée", 1600);
    } catch (err) {
      console.error("Erreur suppression LM soumise :", err);
      setLmSoumisData(previous);
      dialog.alert("Erreur lors de la suppression de la LM soumise.");
    }
  };

  const shortcuts = [
    {
      key: "mails",
      icon: Mail,
      label: "Mails",
      badge: gmail.connected ? unreadCount : 0,
      onClick: () => onNav && onNav("mails"),
    },
    {
      key: "messagerie",
      icon: MessageSquare,
      label: "Messagerie",
      badge: MESSAGERIE_UNREAD_SEED,
      disabled: true,
      onClick: () => flashToast("La messagerie interne sera disponible à l'avenir"),
    },
    {
      key: "discussion",
      icon: MessageCircle,
      label: "Discussion",
      badge: discussionUnreadCount,
      onClick: () => onNav && onNav("discussion"),
    },
    { key: "planning", icon: CalendarDays, label: "Agenda", onClick: () => onNav && onNav("agenda") },
    { key: "temps", icon: Clock, label: "Feuille de temps", onClick: () => onNav && onNav("temps") },
    { key: "conges", icon: CalendarDays, label: "Congés", onClick: () => onNav && onNav("conges") },
  ];

  const handleSuggestSubmit = async ({ type, idea }) => {
    try {
      await httpClient.post("/api/bureau-suggestion", { profileLabel: profMeta.label, type, idea });
      setSuggestOpen(false);
      flashToast("Merci — votre suggestion a été transmise", 2400);
    } catch (err) {
      dialog.alert("Erreur lors de l'envoi de la suggestion.");
    }
  };

  const [dateHover, setDateHover] = useState(false);

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
              Bureau
            </h1>
            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              title="Voir le tutoriel"
              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
            >
              <HelpCircle size={14} />
            </button>
          </div>
          {isAdmin && <ProfileSwitcher profiles={allowedProfiles} active={profile} onChange={setProfile} />}
        </div>
        <div className="relative">
          <button
            onClick={() => setCalOpen((v) => !v)}
            onMouseEnter={() => setDateHover(true)}
            onMouseLeave={() => setDateHover(false)}
            className="flex items-center gap-2 transition-colors capitalize"
            style={{
              background: "#fff",
              border: `1px solid ${dateHover ? BURGUNDY : LINE}`,
              borderRadius: 999,
              padding: "7px 15px",
              fontSize: 11,
              fontWeight: 500,
              color: INK_2,
              boxShadow: dateHover ? "0 2px 8px rgba(110,26,54,.12)" : "none",
            }}
          >
            <CalendarDays size={14} style={{ color: BURGUNDY }} />
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </button>
          {calOpen && <EcheancesCalendarPopover items={cfg.echeances} onClose={() => setCalOpen(false)} />}
        </div>
      </div>

      <div className="grid gap-4 items-start grid-cols-1 min-[1081px]:[grid-template-columns:1fr_260px]">
        <div className="flex flex-col gap-4 min-w-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3.5">
              {profile === "secretariat" ? (
                // Pas de KPI pour ce profil — 4 tuiles "Suggérer une idée"
                // à la place, voir analyse validée avec Jonathan.
                Array.from({ length: 4 }).map((_, i) => (
                  <SuggestTile key={`suggest-${i}`} onClick={() => setSuggestOpen(true)} />
                ))
              ) : (
                <>
                  {cfg.kpis.map((k) => (
                    <KpiTile
                      key={k.key}
                      label={k.label}
                      value={k.value}
                      sub={k.sub}
                      tone={k.tone}
                      placeholder={k.placeholder}
                      loading={isLoading && !k.placeholder}
                      onClick={k.onClick}
                    />
                  ))}
                  <SuggestTile onClick={() => setSuggestOpen(true)} />
                </>
              )}
            </div>

            <div className="grid grid-cols-1 min-[1081px]:[grid-template-columns:5fr_2fr] gap-4 items-start">
              <PriorityFeedCard
                title={cfg.feedTitle}
                items={cfg.feed}
                lateCount={lateCount}
                onSeeAll={cfg.seeAll || (() => openItem(cfg.feed[0] || {}))}
                onOpenItem={openItem}
                onCompleteItem={completeTask}
                loading={isLoading}
                pageSize={cfg.feedPageSize}
              />
              <div className="flex flex-col gap-4 min-w-0">
                {profile === "secretariat" && (
                  <>
                    <RecapCard title="Relances à venir" items={relancesItems} onOpenItem={openItem} loading={isLoading} />
                    <LmSoumisCard
                      items={lmSoumisItems}
                      loading={loadingLmSoumis}
                      onDelete={handleDeleteLmValidation}
                    />
                  </>
                )}
                {profile === "juriste" && (
                  <RecapCard title={cfg.missionsRecapTitle} items={cfg.missionsRecapItems} onOpenItem={openItem} loading={isLoading} />
                )}
                <RecapCard title={cfg.recapTitle} items={cfg.recapItems} onOpenItem={openItem} loading={isLoading} />
                <RdvCard
                  title={cfg.rdvTitle || "Mes rendez-vous à venir"}
                  rdvs={profile === "secretariat" ? cabinetRdvs : upcomingRdvs}
                  onSeeAll={() => onNav && onNav("agenda")}
                  onOpenItem={() => onNav && onNav("agenda")}
                  loading={isLoading}
                  syncingGoogle={loadingGoogleCalendarOverview}
                />
              </div>
            </div>
          </div>
        <div className="flex flex-col gap-4 min-w-0">
          <ShortcutsPanel shortcuts={shortcuts} />
          <NotesBoard onToast={flashToast} />
        </div>
      </div>

      {suggestOpen && (
        <SuggestModal
          profileLabel={profMeta.label}
          onClose={() => setSuggestOpen(false)}
          onSubmit={handleSuggestSubmit}
        />
      )}

      {showTutorial && (
        <TutorialOverlay
          steps={bureauTutorials[profile] || []}
          onClose={() => setShowTutorial(false)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-full shadow-lg z-[300]">
          {toast}
        </div>
      )}
    </div>
  );
}

export default Bureau;
